import { Suspense, useEffect, useRef, useState } from "react";
import { lazyPage, BootSplash } from "./lazyPage";
import { ClerkLoading, SignedIn, SignedOut, SignIn, useAuth, useClerk, useUser } from "@clerk/clerk-react";

// The game (App + every renderer) is its own chunk, fetched when /play actually renders it — so the
// marketing site and the sign-in card never download the 2.9 MB game script.
// A failed or slow chunk never shows a blank page: see lazyPage (the splash while it loads, one reload if a
// deploy has moved the chunk out from under an old tab).
const GameLoaderLazy = lazyPage(() => import("./GameLoader").then((m) => ({ default: m.GameLoader })));
function GameLoader(props: { ready?: boolean }) {
  return <Suspense fallback={<BootSplash label="Loading your garden…" />}><GameLoaderLazy {...props} /></Suspense>;
}
import { Logo } from "./Logo";
import { setAuthTokenGetter, setSaveOwner } from "../sim/save";
import { track } from "../sim/analytics";

/**
 * Auth flag + the /play route gate. The <ClerkProvider> itself lives in main.tsx
 * (wrapping the whole router) so site pages can show signed-in/out state too.
 *
 * `authEnabled` is true when the Clerk publishable key is present (Railway). A
 * no-key build skips Clerk entirely and plays anonymously, so local dev needs no
 * keys — see main.tsx / PlayRoute below.
 */

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
export const authEnabled = !!PUBLISHABLE_KEY;
export const publishableKey = PUBLISHABLE_KEY;

/** Mounted only when signed in: pushes Clerk's token getter AND this player's id into the save store,
 *  then gates the game on both being ready so the first load is authenticated and reads the right mirror.
 *
 *  THE ID MATTERS AS MUCH AS THE TOKEN. The token decides which server row is read; `setSaveOwner` decides
 *  which LOCAL MIRROR key is read and written. Leave the second one out and every account on this browser
 *  shares one mirror — which is how a second player signing up here used to inherit the first one's garden
 *  (the `GET /api/save → 404` import PUTs whatever mirror it finds as the new account's save). `ready`
 *  gates the load on this effect having run, so no mirror is read before the owner is known. */
function AuthedGame() {
  const { getToken, userId } = useAuth();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Clerk's getToken() returns a short-lived JWT the server verifies.
    setAuthTokenGetter(() => getToken());
    setSaveOwner(userId ?? null);
    setReady(true);
    return () => { setAuthTokenGetter(null); setSaveOwner(null); };
  }, [getToken, userId]);
  return <GameLoader ready={ready} />;
}

/** How long sign-in may take to load before the page says something is in the way. */
const SIGN_IN_SLOW_MS = 10_000;

/**
 * While Clerk loads, /play rendered NOTHING (<SignedIn> and <SignedOut> both wait for it) — and a privacy
 * extension or a network that blocks Clerk's script left a blank page forever. Now: the splash, and after
 * SIGN_IN_SLOW_MS a plain line naming the usual cause and a retry.
 */
function SignInLoading() {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const t = window.setTimeout(() => setSlow(true), SIGN_IN_SLOW_MS); return () => window.clearTimeout(t); }, []);
  return (
    <div className="boot-splash" data-testid="signin-loading">
      <div className="boot-title"><Logo height={54} tone="light" /></div>
      <div className="boot-sub">Getting your sign-in ready…</div>
      {slow && (
        <>
          <div className="boot-note">Sign-in is taking a while. An ad blocker or privacy extension can stop it from loading, so try pausing it for this site.</div>
          <button type="button" className="boot-retry" onClick={() => window.location.reload()}>Try again</button>
        </>
      )}
    </div>
  );
}

/** The /play route. Auth build: an account required (signed-out → the combined sign-up / sign-in card,
 *  signed-in → the game). No-auth build: plays anonymously. */
export function PlayRoute() {
  if (!authEnabled) return <GameLoader />;
  return (
    <>
      <ClerkLoading><SignInLoading /></ClerkLoading>
      <SignedOut>
        <div className="auth-screen">
          <div className="auth-hero">
            <h1 className="auth-title"><Logo height={72} tone="light" /></h1>
            <p className="auth-tag">Create a free account or sign in to start your garden.</p>
          </div>
          {/* withSignUp: Clerk's combined flow, so a brand-new player creates an account in this same card instead of
              being sent off-site by the small "Sign up" link. Either way the player lands back here, in the game. */}
          <SignIn routing="hash" withSignUp forceRedirectUrl="/play" signUpForceRedirectUrl="/play" />
          <SignInCardShown />
        </div>
      </SignedOut>
      <SignedIn>
        <AuthedGame />
      </SignedIn>
    </>
  );
}

/** /play's sign-in card is a prompt too (it is not opened through `openSignIn`): one auth_prompt per showing. */
function SignInCardShown() {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    track("auth_prompt", { mode: "sign_in" });
  }, []);
  return null;
}

/** A sign-in newer than this when the page first sees it counts as just finished (not a session restored days later). */
const AUTH_FRESH_MS = 10 * 60_000;
/** The last sign-in time already reported, so a reload or a second tab does not report it again. */
const AUTH_SEEN_KEY = "shelf-garden.analytics.authSeen";

/**
 * SIGN-IN AND SIGN-UP, FOR ANALYTICS (docs/ANALYTICS.md: `auth_prompt`, `auth_done`). Mounted once inside
 * <ClerkProvider> (main.tsx).
 *
 * PROMPTS. Every "Sign in" / "Create an account" button on the site is Clerk's <SignInButton>/<SignUpButton> in
 * modal mode, and each opens through the shared Clerk instance's `openSignIn` / `openSignUp` — so those two are
 * wrapped here, once, instead of instrumenting a dozen buttons (and every one added later).
 *
 * COMPLETIONS. The user's `lastSignInAt`, when it is recent and newer than the one last reported, is one finished
 * sign-in — read on any page, so the OAuth round trip (which returns as a fresh page load, already signed in) counts
 * too. It is a sign-UP when the account was created in the same few minutes.
 */
export function AuthAnalytics() {
  const clerk = useClerk() as unknown as Record<string, unknown>;
  const { user } = useUser();
  useEffect(() => {
    if (clerk.__sgTracked) return;
    clerk.__sgTracked = true;
    for (const [fn, mode] of [["openSignIn", "sign_in"], ["openSignUp", "sign_up"]] as const) {
      const open = clerk[fn];
      if (typeof open !== "function") continue;
      clerk[fn] = (...args: unknown[]) => {
        track("auth_prompt", { mode });
        return (open as (...a: unknown[]) => unknown)(...args);
      };
    }
  }, [clerk]);
  const at = (user?.lastSignInAt ?? user?.createdAt)?.getTime() ?? 0;
  const created = user?.createdAt?.getTime() ?? 0;
  useEffect(() => {
    if (!at || Date.now() - at > AUTH_FRESH_MS) return;
    let seen = 0;
    try { seen = Number(localStorage.getItem(AUTH_SEEN_KEY)) || 0; } catch { /* storage blocked */ }
    if (at <= seen) return;
    try { localStorage.setItem(AUTH_SEEN_KEY, String(at)); } catch { /* storage blocked: may count twice */ }
    track("auth_done", { mode: created && Math.abs(at - created) < AUTH_FRESH_MS ? "sign_up" : "sign_in" });
  }, [at, created]);
  return null;
}
